'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import SiteHeader from '@/components/SiteHeader';
import { useSiteSettings } from '@/lib/swr';
import styles from './about.module.css';

const sections = [
  { id: 'lab', label: 'Лаборатория' },
  { id: 'activities', label: 'Направления' },
  { id: 'university', label: 'Университет' },
  { id: 'institute', label: 'Институт' },
  { id: 'contact', label: 'Связаться' },
];

const activities = [
  { title: 'Машинное обучение', description: 'Разработка и обучение моделей искусственного интеллекта для прикладных задач.', tools: 'Deep Learning / Neural Networks / MLOps' },
  { title: 'Компьютерное зрение', description: 'Обработка изображений и видео, распознавание объектов и визуальный анализ.', tools: 'OpenCV / CNN / Object Detection' },
  { title: 'Обработка языка', description: 'Анализ текстов и создание систем понимания естественного языка.', tools: 'NLP / Transformers / BERT' },
  { title: 'Анализ данных', description: 'Поиск закономерностей и извлечение знаний из массивов данных.', tools: 'Big Data / Statistics / Visualization' },
  { title: 'Разработка ПО', description: 'Программные решения, которые соединяют модели, данные и пользовательские сценарии.', tools: 'Python / JavaScript / Cloud' },
  { title: 'Образование', description: 'Подготовка специалистов через обучение, практику и участие в исследованиях.', tools: 'Курсы / Стажировки / Практика' },
];

export default function AboutPage() {
  const { settings: siteSettings, isLoading: settingsLoading } = useSiteSettings();

  useEffect(() => {
    // Авторские тексты меняют высоту страницы: открываем якорь после загрузки настроек.
    if (settingsLoading) return;
    const scrollToAnchor = () => {
      const section = window.location.hash.slice(1);
      if (sections.some(item => item.id === section)) {
        document.getElementById(section)?.scrollIntoView({ behavior: 'auto' });
      }
    };
    scrollToAnchor();
    window.addEventListener('hashchange', scrollToAnchor);
    return () => window.removeEventListener('hashchange', scrollToAnchor);
  }, [settingsLoading]);

  const description = siteSettings?.about_lab_description || 'ЛИСАД — научно-исследовательская лаборатория, специализирующаяся на обучении моделей искусственного интеллекта и разработке программ с применением ИИ.\n\nМы занимаемся фундаментальными и прикладными исследованиями в области машинного обучения, компьютерного зрения, обработки естественного языка и анализа данных.';

  return (
    <div className={styles.page}>
      <SiteHeader />
      <main className={styles.content}>
        <header className={styles.hero}>
          <div className={styles.heroTop}>
            <span className={styles.eyebrow}>ЛИСАД / КБГУ</span>
            <span className={styles.location}>Нальчик, Россия</span>
          </div>
          <div className={styles.heroGrid}>
            <div>
              <h1>{siteSettings?.about_lab_title || 'Лаборатория интеллектуальных систем и анализа данных'}</h1>
              <p className={styles.heroLead}>{siteSettings?.about_lab_subtitle || 'Научно-исследовательская лаборатория Института электроники, робототехники и искусственного интеллекта КБГУ им. Х.М. Бербекова'}</p>
            </div>
            <div className={styles.heroAside}>
              <span className={styles.asideLabel}>Наука в действии</span>
              <p>От исследовательского вопроса —<br />к инженерному решению.</p>
              <Link href="/projects" className={styles.textLink}>Исследовать проекты <span aria-hidden="true">↗︎</span></Link>
            </div>
          </div>
          <nav className={styles.sectionNav} aria-label="Разделы страницы о лаборатории">
            {sections.map(section => <a key={section.id} href={`#${section.id}`}>{section.label}</a>)}
          </nav>
        </header>

        <section id="lab" className={styles.section} aria-labelledby="lab-title">
          <div className={styles.sectionIntro}>
            <div><span className={styles.eyebrow}>О лаборатории</span><h2 id="lab-title">Исследуем.<br />Разрабатываем.<br />Применяем.</h2></div>
            <div className={styles.prose}>{description.split(/\r?\n|\\r\\n|\\n/).filter(p => p.trim()).map((paragraph, index) => <p key={index}>{paragraph.trim()}</p>)}</div>
          </div>
          <dl className={styles.principles}>
            {[
              ['Миссия', siteSettings?.about_lab_mission || 'Развитие ИИ-технологий для решения реальных задач'],
              ['Видение', siteSettings?.about_lab_vision || 'Соединять исследования, инженерную практику и образование'],
              ['Ценности', siteSettings?.about_lab_values || 'Инновации, качество, открытость, сотрудничество'],
            ].map(([title, text]) => <div key={title}><dt>{title}</dt><dd>{text}</dd></div>)}
          </dl>
          <div className={styles.resourceLinks}>
            <Link href="/projects"><div><h3>Проекты лаборатории</h3><p>Задачи, участники и текущее состояние опубликованных проектов.</p></div><span aria-hidden="true">↗︎</span></Link>
            <Link href="/news"><div><h3>События и результаты</h3><p>Исследования, мероприятия и новости команды — из первых рук.</p></div><span aria-hidden="true">↗︎</span></Link>
          </div>
        </section>

        <section id="activities" className={styles.section} aria-labelledby="activities-title">
          <div className={styles.sectionIntro}>
            <div><span className={styles.eyebrow}>Области работы</span><h2 id="activities-title">Направления<br />деятельности</h2></div>
            <p className={styles.introText}>Технологии, с которыми работают лаборатория и институт. От моделей и данных — до программных решений и образовательной практики.</p>
          </div>
          <ol className={styles.activityList}>
            {activities.map((activity, index) => <li key={activity.title}>
              <span className={styles.activityIndex} aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
              <div><h3>{activity.title}</h3><p>{activity.description}</p><span className={styles.toolset}>{activity.tools}</span></div>
            </li>)}
          </ol>
        </section>

        <section id="university" className={styles.section} aria-labelledby="university-title">
          <div className={styles.sectionIntro}>
            <div><span className={styles.eyebrow}>Академическая среда</span><h2 id="university-title">Кабардино-Балкарский государственный университет</h2></div>
            <div className={styles.prose}><p>Лаборатория работает в КБГУ имени Х.М. Бербекова — университете в Нальчике, объединяющем образование, исследовательскую работу и подготовку специалистов.</p><p>Научные школы, образовательные программы и междисциплинарное сотрудничество создают среду для совместных проектов студентов и исследователей.</p></div>
          </div>
          <div className={styles.historyLine}><p><span>Университет с</span> <strong>1957</strong><span>5 апреля — преобразование в университет</span></p><a href="https://kbsu.ru/podrazdelenija/inye-podrazdeleniya/muzej-istorii-kbgu/" target="_blank" rel="noopener noreferrer" className={styles.textLink}>История КБГУ — официальный источник <span aria-hidden="true">↗︎</span></a></div>
        </section>

        <section id="institute" className={styles.section} aria-labelledby="institute-title">
          <div className={styles.sectionIntro}>
            <div><span className={styles.eyebrow}>Институт</span><h2 id="institute-title">Электроника.<br />Робототехника.<br />Искусственный интеллект.</h2></div>
            <div className={styles.prose}><p>Институт электроники, робототехники и искусственного интеллекта — структурное подразделение КБГУ, в котором работает ЛИСАД.</p><p>Институт готовит специалистов и проводит исследования в профильных областях: от электронных и роботизированных систем до искусственного интеллекта и разработки программного обеспечения.</p><p className={styles.disciplines}>Электроника / Робототехника / ИИ / Программирование</p></div>
          </div>
        </section>

        <section id="contact" className={`${styles.section} ${styles.contact}`} aria-labelledby="contact-title">
          <div><span className={styles.eyebrow}>Открыты к диалогу</span><h2 id="contact-title">Свяжитесь с нами</h2><p className={styles.introText}>Обсудим исследовательскую задачу, совместный проект или участие в работе лаборатории.</p></div>
          <div className={styles.contactActions}><Link href="/messages?support=1" className={styles.primaryAction}>Написать нам <span aria-hidden="true">↗︎</span></Link><Link href="/register" className={styles.textLink}>Стать частью команды <span aria-hidden="true">↗︎</span></Link></div>
        </section>
        <footer className={styles.footer}><span>ЛИСАД · Институт электроники, робототехники и ИИ · КБГУ им. Х.М. Бербекова</span><span>Нальчик, Кабардино-Балкарская Республика</span></footer>
      </main>
    </div>
  );
}
